import React, { useEffect, useRef, useState } from 'react';
import { Account } from '../../../types';
import { Text, TextBlock } from '../../Atoms/Text';
import { Checkbox, FormControlLabel, Switch } from '@mui/material';
import {
  bulkUpdateSharedAccountCustomizations,
  defaultSharedAccountCustomizationData,
  fetchSharedAccountCustomizations,
  getSharedAccountCustomizationsSubject,
  SharedAccountCustomizationData
} from '../../../store/SharedAccountCustomizations';
import { AccountSelectionForm } from '../../Compounds/UserCreation';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { emailRegex } from '../../../utils/helpers';
import CompanySettingsBulkSaveDialog from './CompanySettingsBulkSaveDialog';
import AdditionalRecipients from './AdditionalRecipients';
import { fetchMyTeam } from '../../../Api/MyTeam/fetchMyTeam';
import { getUser } from '../../../store/User';
import { useNavigate } from 'react-router-dom';
import { TeamMember } from '../../../store/GeneralStore';
import { TagsTable } from '../../Compounds/Tagging';
import { fetchTags } from '../../../store/Tags';
import { FeatureFlags, defaultFeatureFlags, getFeatureFlagSubject } from '../../../Api/useFeatureFlags';
import { fetchProjects } from '../../../store/Project';
import {
  ColumnNameCustomizationSection,
  ColumnNameCustomizationSectionHandle
} from '../../Compounds/CustomFieldsForm/ColumnNameCustomizationSection';

const switchSx = {
  '.Mui-checked': {
    color: '#3B6FE8 !important'
  },
  '.MuiSwitch-track': {
    backgroundColor: '#878EBE !important'
  }
};

const CompanySettingsTab: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedSingleAccount, setSelectedSingleAccount] = useState<Account | null>(null);
  const [selectedBulkAccounts, setSelectedBulkAccounts] = useState<Account[]>([]);
  const [additionalRecipInput, setAdditionalRecipInput] = useState<string>();
  const [sharedCustomizations, setSharedCustomizations] = useState<SharedAccountCustomizationData[]>([]);
  const [selectedSharedCustomization, setSelectedSharedCustomization] = useState<SharedAccountCustomizationData>();
  const [bulkModal, setBulkModal] = useState<boolean>(false);
  const [owner, setOwner] = useState<TeamMember | null>(null);
  const [csm, setCsm] = useState<TeamMember | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(defaultFeatureFlags);
  const columnNameRef = useRef<ColumnNameCustomizationSectionHandle>(null);
  const { setSnack } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    setAccounts(getUser().accounts);
    const sharedCustomizationsSub = getSharedAccountCustomizationsSubject().subscribe((data) => {
      setSharedCustomizations(data);
    });
    const featureFlagSub = getFeatureFlagSubject().subscribe((flags) => setFeatureFlags(flags));

    fetchSharedAccountCustomizations();

    return () => {
      if (sharedCustomizationsSub) sharedCustomizationsSub.unsubscribe();
      if (featureFlagSub) featureFlagSub.unsubscribe();
      // reset tag store to current account context, refetch entities with tags
      fetchTags(true);
      fetchProjects(true);
    };
  }, []);

  const handleBulkAccountChange = (e: React.SyntheticEvent<Element, Event>, value: string[] | null) => {
    const filteredAccounts = accounts.filter((a) => value?.includes(a.name));
    setSelectedBulkAccounts(filteredAccounts || []);
    if (value && selectedBulkAccounts.length === 0) {
      const account = filteredAccounts[0];
      const sharedC = sharedCustomizations.find((i) => i.account_id === account.id);
      setSelectedSharedCustomization(sharedC || defaultSharedAccountCustomizationData());
    }
  };

  const handleAdd = () => {
    if (additionalRecipInput && emailRegex.test(additionalRecipInput)) {
      if (!selectedSharedCustomization?.content.additionalRecipients?.includes(additionalRecipInput)) {
        selectedSharedCustomization?.content.additionalRecipients?.push(additionalRecipInput);
      }
      setAdditionalRecipInput('');
    }
  };

  const onAdditionalRecipientsDelete = (email: string) => () => {
    setSelectedSharedCustomization((currentCust) => {
      if (!currentCust?.content?.additionalRecipients) return currentCust;
      const emails = currentCust.content.additionalRecipients.filter((v) => v !== email);
      return { ...currentCust, content: { ...currentCust.content, additionalRecipients: emails } };
    });
  };

  useEffect(() => {
    if (selectedSingleAccount) {
      const sharedC = sharedCustomizations.find((i) => i.account_id === selectedSingleAccount.id);
      if (sharedC) {
        setSelectedSharedCustomization(sharedC);
      }
    }
  }, [sharedCustomizations, selectedSingleAccount]);

  const handleSingleAccountChange = (acct: Account | undefined) => {
    setOwner(null);
    setCsm(null);
    if (acct) {
      getTeam(acct);
      setSelectedSingleAccount(acct);
      setSelectedBulkAccounts([acct]);
      const sharedC = sharedCustomizations.find((i) => i.account_id === acct.id);
      setSelectedSharedCustomization(sharedC || defaultSharedAccountCustomizationData());
      fetchSharedAccountCustomizations([acct.id]);
      fetchTags(true, acct.id);
    }
  };

  const getTeam = async (acct: Account) => {
    const { data } = await fetchMyTeam(acct.id);

    if (data !== null) {
      setOwner(data.owner);
      setCsm(data.success_manager);
    }
  };

  const saveAccountCust = async () => {
    if (selectedSharedCustomization?.content?.additionalRecipients) {
      const res = await bulkUpdateSharedAccountCustomizations(selectedBulkAccounts, selectedSharedCustomization);

      if (res) {
        try {
          if (columnNameRef.current?.isDirty()) {
            const completed = await columnNameRef.current.save();
            // A declined incompatible-values dialog stops the save; keep the modal open, no snack.
            if (!completed) return;
          }
          setSnack({
            message: 'Settings Saved',
            open: true,
            type: 'success'
          });
          closeModal();
          fetchSharedAccountCustomizations(selectedBulkAccounts.map((a) => a.id));
        } catch (err) {
          setSnack({
            type: 'error',
            message: err instanceof Error ? err.message : 'Failed to save column settings.',
            open: true
          });
        }
      } else {
        setSnack({
          type: 'error',
          message: 'Failed to save settings.',
          open: true
        });
      }
    }
  };

  const closeModal = () => {
    setBulkModal(false);
    setSelectedBulkAccounts(selectedSingleAccount ? [selectedSingleAccount] : []);
  };

  const toggleTeamMember = (email: string) => {
    if (!email) return;
    if (selectedSharedCustomization?.content.additionalRecipients?.includes(email)) {
      onAdditionalRecipientsDelete(email)();
    } else {
      setSelectedSharedCustomization((currentCust) => {
        if (!currentCust?.content?.additionalRecipients) return currentCust;
        currentCust?.content?.additionalRecipients.push(email);
        return {
          ...currentCust,
          content: { ...currentCust.content, additionalRecipients: currentCust?.content?.additionalRecipients }
        };
      });
    }
  };

  const hideBulkSave = () => {
    const user = getUser();
    const roles = ['Admin', 'Support', 'Advisor', 'Customer Success Manager', 'Service Delivery Manager', 'Executive'];
    return user.roles.some((i) => roles.includes(i.name));
  };

  const handleToggleTaxExclusion = () => {
    setSelectedSharedCustomization((currentCust) => {
      if (!currentCust) return currentCust;
      const currentSetting =
        currentCust.content.excludeInvoiceParserTaxes || currentCust.content.excludeInvoiceParserTaxes === undefined;
      return {
        ...currentCust,
        content: { ...currentCust.content, excludeInvoiceParserTaxes: !currentSetting }
      };
    });
  };

  return (
    <div>
      <div className="flex mt-5 px-1 w-[50%]">
        <AccountSelectionForm
          accounts={accounts}
          selectedAccount={selectedSingleAccount}
          setSelectedAccount={handleSingleAccountChange}
          size="small"
          styles="w-full"
        />
      </div>

      <div className="flex mt-5 px-1">
        <Text
          className="font-semibold w-full"
          color="grey9"
          size="sm">
          Support Cases
        </Text>
      </div>

      <hr className="my-2 h-0.5 border-t-1" />

      <div className="flex mt-5 px-1">
        <Text
          className="w-full"
          color="grey9"
          size="sm">
          Default Additional Recipients
        </Text>
      </div>

      <AdditionalRecipients
        additionalRecipients={selectedSharedCustomization?.content.additionalRecipients}
        handleAdd={handleAdd}
        additionalRecipInput={additionalRecipInput}
        setAdditionalRecipInput={setAdditionalRecipInput}
        saveDefault={false}
        onAdditionalRecipientsDelete={onAdditionalRecipientsDelete}
        showDefaultBox={false}
        styles={{
          divClassName: 'flex mt-5 px-1',
          chip: {
            marginBottom: '.5rem',
            borderRadius: '5px',
            marginRight: '.5rem'
          }
        }}
      />

      {/* Gated on the address, not the person: the API always returns the slot, and a recipient with no address cannot be added. */}
      {owner?.email && (
        <div className="flex mt-5 px-1">
          <FormControlLabel
            className="w-full"
            control={
              <Checkbox
                checked={selectedSharedCustomization?.content.additionalRecipients?.includes(owner.email)}
                onClick={() => toggleTeamMember(owner.email)}
              />
            }
            label={`Include ${owner.title}: ${owner.email}`}
          />
        </div>
      )}

      {csm?.email && (
        <div className="flex mt-5 px-1">
          <FormControlLabel
            className="w-full"
            control={
              <Checkbox
                checked={selectedSharedCustomization?.content.additionalRecipients?.includes(csm.email)}
                onClick={() => toggleTeamMember(csm.email)}
              />
            }
            label={`Include ${csm.title}: ${csm.email}`}
          />
        </div>
      )}

      <>
        <TextBlock
          size="md"
          weight="semibold"
          className="mt-4">
          Invoice Parser
        </TextBlock>
        <hr className="mt-3 h-0.5 border-t-2" />

        <div className="flex mt-5 px-1">
          <div>
            <Text
              color="grey6"
              weight="normal"
              size="sm">
              Tax Exclusion
            </Text>
          </div>
          <div className="flex !ml-auto items-center space-x-3">
            <div>
              <Switch
                sx={switchSx}
                disabled={!selectedSharedCustomization}
                size="small"
                checked={
                  selectedSharedCustomization?.content.excludeInvoiceParserTaxes ||
                  selectedSharedCustomization?.content.excludeInvoiceParserTaxes === undefined ||
                  false
                }
                onChange={handleToggleTaxExclusion}
              />
            </div>
          </div>
        </div>
      </>

      {selectedSingleAccount && (
        <ColumnNameCustomizationSection
          ref={columnNameRef}
          accountId={selectedSingleAccount.id}
        />
      )}

      {featureFlags.projects && (
        <>
          <TextBlock
            size="md"
            weight="semibold"
            className="mt-4">
            Tag Management
          </TextBlock>
          <hr className="mt-3 h-0.5 border-t-2" />

          {(selectedSingleAccount || selectedBulkAccounts.length === 1) && (
            <TagsTable accountId={selectedSingleAccount?.id || selectedBulkAccounts[0]?.id} />
          )}
          {!selectedSingleAccount && selectedBulkAccounts.length === 0 && (
            <TextBlock
              size="sm14"
              className="mt-4">
              Select an account to manage tags.
            </TextBlock>
          )}
          {!selectedSingleAccount && selectedBulkAccounts.length > 1 && (
            <TextBlock
              size="sm14"
              className="mt-4">
              Select a single account to manage tags.
            </TextBlock>
          )}
        </>
      )}

      <hr className="mt-3 h-0.5 border-t-2" />

      <div className="justify-between mt-5">
        <button
          onClick={() => navigate('/')}
          className={DEFAULT_CANCEL_BUTTON_STYLE}>
          Back
        </button>

        <button
          onClick={saveAccountCust}
          className={`${DEFAULT_ADVANCE_BUTTON_STYLE} float-right`}>
          Save
        </button>

        {!hideBulkSave() && (
          <button
            onClick={() => setBulkModal(true)}
            className="float-right mr-8 mt-2 text-[indigo] text-sm">
            Bulk Save
          </button>
        )}
      </div>

      <CompanySettingsBulkSaveDialog
        open={bulkModal}
        onClose={closeModal}
        accounts={accounts}
        selectedBulkAccounts={selectedBulkAccounts}
        handleAccountSelect={handleBulkAccountChange}
        saveAccountCust={saveAccountCust}
      />
    </div>
  );
};

export default CompanySettingsTab;
